import { AdditionalChannelEnum, ChannelEnum, UIChannelEnum, WhatsAppStatusEnum } from '../auth/types/LoginRequest';
import { GetMFAConnectionResponse } from '../auth/types/LoginResponse';
import { CustomerType } from '../selfServices/types/SelfService';
import { ADDITIONAL_CHANNEL_MAP, CHANNEL_TO_ADDITIONAL } from './constants';
import { CommunicationState } from './types/CommunicationState';

type SendMFAPayload = { value: ChannelEnum; customerType?: CustomerType };


export const normalizeChannel = (channel: UIChannelEnum): ChannelEnum =>
  ADDITIONAL_CHANNEL_MAP[channel as AdditionalChannelEnum] ?? (channel as ChannelEnum);

const isWhatsAppUsable = (data: CommunicationState | GetMFAConnectionResponse): boolean =>{
  const { phoneNumber, additionalCustomerPhoneNumber, whatsApp } = data;
  const hasPhoneNumber = !!phoneNumber || !!additionalCustomerPhoneNumber;
  return Boolean(hasPhoneNumber && whatsApp?.enabled && whatsApp?.status === WhatsAppStatusEnum.REACHABLE);
};

export const getAvailableChannels = (data: CommunicationState | GetMFAConnectionResponse): UIChannelEnum[] => {
  const channels: UIChannelEnum[] = [];
  const { phoneNumber, email, additionalCustomerPhoneNumber, additionalCustomerEmail } = data;
  const canUseWhatsapp = isWhatsAppUsable(data);

  if (phoneNumber) {
    channels.push(ChannelEnum.SMS);
    if (canUseWhatsapp) {
      channels.push(ChannelEnum.WHATSAPP);
    }
  }
  if (email) {
    channels.push(ChannelEnum.EMAIL);
  }
  if (additionalCustomerPhoneNumber) {
    channels.push(AdditionalChannelEnum.SMS_ADDITIONAL);
    if (canUseWhatsapp) {
      channels.push(AdditionalChannelEnum.WHATSAPP_ADDITIONAL);
    }
  }
  if (additionalCustomerEmail) {
    channels.push(AdditionalChannelEnum.EMAIL_ADDITIONAL);
  }
  return channels;
};

export const getDefaultChannelToSendCode =
  (data: CommunicationState | GetMFAConnectionResponse, blacklist: UIChannelEnum[] = []): ChannelEnum => {
    const channels = getAvailableChannels(data).filter((channel) => !blacklist.includes(channel));
    const { interventionLastMessageChannel } = data || {};
    if (interventionLastMessageChannel && channels.some((channel) => normalizeChannel(channel) === interventionLastMessageChannel)) {
      return interventionLastMessageChannel;
    }
    return normalizeChannel(channels[0]);
  };

export const getDefaultUIChannelToSendCode =
  (data: CommunicationState, blacklist: UIChannelEnum[] = []): UIChannelEnum => {
    const available = getAvailableChannels(data).filter((channel) => !blacklist.includes(channel));
    const { lastUsedUIChannel } = data;
    if (lastUsedUIChannel && available.includes(lastUsedUIChannel)) {
      return lastUsedUIChannel;
    }
    return available[0];
  };

export const getRetryExpiration = (data: CommunicationState | GetMFAConnectionResponse, channel: UIChannelEnum) => {
  switch (normalizeChannel(channel)) {
    case ChannelEnum.SMS:
      return data.smsRetryExpiration;
    case ChannelEnum.EMAIL:
      return data.emailRetryExpiration;
    case ChannelEnum.WHATSAPP:
      return data.whatsAppRetryExpiration;
    default:
      console.error('Unknown channel type:', channel);
      return undefined;
  }
};

type CommunicationContactField = keyof Pick<
  CommunicationState,
  'email' | 'phoneNumber' | 'additionalCustomerEmail' | 'additionalCustomerPhoneNumber'
>;

const CONTACT_INFO_FIELD_BY_CHANNEL: Record<UIChannelEnum, CommunicationContactField> = {
  [ChannelEnum.EMAIL]: 'email',
  [ChannelEnum.SMS]: 'phoneNumber',
  [ChannelEnum.WHATSAPP]: 'phoneNumber',
  [AdditionalChannelEnum.EMAIL_ADDITIONAL]: 'additionalCustomerEmail',
  [AdditionalChannelEnum.SMS_ADDITIONAL]: 'additionalCustomerPhoneNumber',
  [AdditionalChannelEnum.WHATSAPP_ADDITIONAL]: 'additionalCustomerPhoneNumber',
};

export const getContactInfoForChannel = (
  data: CommunicationState,
  channel: ChannelEnum,
  customerType?: CustomerType,
): string | undefined => {
  const resolvedChannel: UIChannelEnum = customerType === CustomerType.ADDITIONAL_CUSTOMER
    ? CHANNEL_TO_ADDITIONAL[channel]
    : channel;
  return data[CONTACT_INFO_FIELD_BY_CHANNEL[resolvedChannel]];
};

export const getMFAPayload = (channel: UIChannelEnum): SendMFAPayload => {
  const normalizedChannel = ADDITIONAL_CHANNEL_MAP[channel as AdditionalChannelEnum];
  if (normalizedChannel) {
    return { value: normalizedChannel, customerType: CustomerType.ADDITIONAL_CUSTOMER };
  }
  return { value: channel as ChannelEnum, customerType: CustomerType.CUSTOMER };
};

export const formatPhoneNumber = (phoneNumber: string | { prefix: string, local: string } | undefined): string | undefined => {
  if (typeof phoneNumber === 'string') {
    return phoneNumber;
  }
  if (!phoneNumber?.local) {
    return undefined;
  }
  return `+${phoneNumber.prefix} ${phoneNumber.local}`;
};
